<template>
  <div
    class="slide-search"
    :class="{
      'h-full': textSearch
    }"
  >
    <div class="slide-search-header">
      <div
        class="flex items-center justify-center back-search"
        @click.stop="backToChoiceType"
        v-if="textSearch"
      >
        <i class="fas fa-angle-left fa-2x"></i>
      </div>
      <aha-input
        class="w-full"
        id="slide-search-input"
        :placeholder="$i18nPresenter.t('Search the question bank...')"
        v-model="textSearch"
        @input="onChangeInputSearch"
        :maxLength="100"
        @focus="focusInputSearch"
        hideCharacterCounter
      >
        <template #append>
          <aha-icon
            name="search"
            class="absolute transform -translate-y-1/2 top-1/2 text-base-60 left-3"
            width="22"
            height="22"
            viewBox="0 0 24 24"
          />
        </template>
      </aha-input>
    </div>

    <div
      v-if="textSearch"
      class="search-result"
    >
      <SlideSearchFilter
        v-if="textSearch"
        :selectedType="selectedType"
        @changeFilter="updateFilter"
      />
      <div v-if="isSearching && !isLoadingMore" class="w-full text-center">
        <InlineLoader :additionalClasses="'mt-48'" />
      </div>
      <div v-else>
        <SlideSearchNoResult
          v-if="textSearch && !slideResults.length && isInputDirty"
          :keyword="textSearch"
          class="mt-40"
        />
        <SlideSearchResultItem
          v-else
          class="mb-4 search-slide-item"
          v-for="item in slideResults"
          :key="item.id"
          :slide="item"
          :textSearch="textSearch"
          :selectedType="selectedType"
          :page="page"
          :isLocking="isLocking"
          @addedSlide="handleAddedSlide"
          @lockAddSlide="handleLockAddSlide"
          @unlockAddSlide="handleUnlockAddedSlide"
          :isSubmittingMultiple="isSubmittingMultiple"
        />
        <div v-if="isLoadMore" class="w-full mb-4 text-center">
          <aha-button @click="loadMore" :submitting="isLoadingMore" :disabled="isLoadingMore">
            {{ $i18nPresenter.t('Load more...') }}
          </aha-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import SlideSearchResultItem from './SlideSearchResultItem.vue'
import SlideSearchFilter from './SlideSearchFilter.vue'
import SlideSearchNoResult from './SlideSearchNoResult.vue'
import InlineLoader from '@/components-v2/common/loading/InlineLoader.vue'

import SearchApi from '@/api/search'
import utils from '@/util'
import EventBus from '@/eventBus'

const eventName = 'Slide Template'
const ALL_SLIDE_TYPES = 'All slide types'

export default {
  components: {
    SlideSearchFilter,
    InlineLoader,
    SlideSearchNoResult,
    SlideSearchResultItem,
  },
  data() {
    return {
      isSubmittingMultiple: false,
      isInputDirty: false,
      allTypeSections: [],
      textSearch: '',
      allTypeSlides: [{
        type: 'all',
        name: ALL_SLIDE_TYPES,
      }],
      selectedType: {
        name: ALL_SLIDE_TYPES,
        filter: [],
      },
      slideResults: [],
      page: 1,
      limit: 10,
      isLoadMore: false,
      isSearching: false,
      isLoadingMore: false,
      isLocking: false,
    }
  },
  watch: {
    textSearch: {
      handler() {
        this.$emit('search', this.textSearch)
      },
    },
  },
  created() {
    EventBus.$on('slide-search-popup:unlock-add-slide', this.handleUnlockAddedSlide)
  },
  mounted() {
    this.autoFocusInputSearch()
  },
  destroyed() {
    EventBus.$off('slide-search-popup:unlock-add-slide', this.handleUnlockAddedSlide)
  },
  methods: {
    handleAddedSlide() {
      this.isSubmittingMultiple = true
    },

    handleLockAddSlide() {
      this.isLocking = true
    },

    handleUnlockAddedSlide() {
      this.isLocking = false
    },

    autoFocusInputSearch() {
      /* autofocus prop just work with input, not aha-input, so selector may do the trick */
      const el = document.querySelector('#slide-search-input input')
      el.focus()
    },

    backToChoiceType() {
      this.textSearch = ''
      this.selectedType = {
        name: ALL_SLIDE_TYPES,
        filter: [],
      }
      utils.mixpanelTracking(eventName, 'Back To Slide Type', '')
    },

    updateFilter(filter) {
      this.selectedType = filter
      this.page = 1
      this.handleSearchSlide()
      utils.mixpanelTracking(eventName, 'Filter Slide Type', filter.name)
    },

    handleSearchSlide: _.debounce(async function debounceSearchSlide() {
      if (!this.textSearch) {
        this.slideResults = []
        this.isInputDirty = false
        this.selectedType = {
          name: ALL_SLIDE_TYPES,
          filter: [],
        }
        return
      }
      const types = []
      if (this.selectedType && this.selectedType.filter && this.selectedType.filter.length) {
        this.selectedType.filter.forEach((item) => {
          types.push(item.type)
        })
      }
      const payload = {
        q: this.textSearch,
        page: this.page,
      }

      if (types.length) {
        payload.types = types.toString()
      }
      this.isSearching = true
      await this.searchSlide(payload)
      this.isInputDirty = true
      this.isSearching = false
    }, 300),

    async searchSlide(payload) {
      try {
        const resp = await SearchApi.searchSlide(payload)
        resp.body.slides.forEach((slide) => {
          if (slide.type === 'pickAnswer' && slide.slideType === 'typeAnswer' && slide.otherCorrectQuiz) {
            slide.otherCorrectQuiz = JSON.parse(slide.otherCorrectQuiz)
          }
        })
        if (this.isLoadingMore) {
          this.slideResults = this.slideResults.concat(resp.body.slides)
        } else {
          this.slideResults = resp.body.slides
        }
        if (resp.body.slides.length < this.limit) {
          this.isLoadMore = false
        } else {
          this.page++
          this.isLoadMore = true
        }
        if (!resp.body.slides.length) {
          payload.noResult = true
        }
        utils.mixpanelTracking(eventName, 'Search Slide', payload.q, '', payload)
      } catch {
        // do nothing
      }
      this.isLoadingMore = false
    },

    async loadMore() {
      if (this.isLoadingMore) return
      this.isLoadingMore = true
      this.handleSearchSlide()
      utils.mixpanelTracking(eventName, 'Click load more', '')
    },

    onChangeInputSearch() {
      this.page = 1
      this.handleSearchSlide()
    },

    focusInputSearch() {
      utils.mixpanelTracking(eventName, 'Focus Input Search', '')
    },
  },
}
</script>

<style lang="scss" scoped>
.slide-search-header {
  padding: 20px;
  background-color: #4F9BF3;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
}

.back-search {
  cursor: pointer;
  padding-right: 16px;
  color: #D1D1D1;
}

::v-deep {
  .back-search {
    color: #fff;
  }

  .aha-input {
    padding-right: 8px;
    padding-left: 40px;
    font-size: 1rem;
    line-height: 1.5rem;

    &::placeholder {
      color: #282E3E;
    }
  }
}

.search-result {
  background-color: #F0F4FA;
  height: calc(100% - 80px);
  overflow-y: auto;
  padding: 1rem;
}
</style>
